import React, { useState, useEffect } from 'react';
import './index.scss';
import { formatCompactDateString, parseCompactDate } from '@sg/utils/common';
import type { ProjectCalendarProps } from './types';
import clsx from 'clsx';
import moment from 'moment';

const ProjectCalendar: React.FC<ProjectCalendarProps> = ({projects, onCardClick, isEditing = false, selectedMonth}) => {
  const ROW_SIZE = 6;
  // Check if there's no data
  if (!projects || projects.length === 0) {
    return (
        <div className="dbs-project-calendar-empty">
          Data not applicable or not available
        </div>
    );
  }

  // State for tracking which card is currently selected/active
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

  // Determine the reference date: use selectedMonth if provided, otherwise use today
  const referenceDate = (() => {
    if (selectedMonth) {
      // Parse "YYYY-MM" format and set to last day of that month
      return moment(selectedMonth, 'YYYY-MM').endOf('month').toDate();
    } else {
      // Use today's date
      return moment().startOf('day').toDate();
      // Use mock date for local verification
      // return moment('2025-10-31', 'YYYY-MM-DD').startOf('day').toDate();
    }
  })();

  // Since projects are sorted, find the index of the last project in the past or current period.
  const lastPastProjectIndex = projects
    .map(p => parseCompactDate(p.date))
    .map(date => date <= referenceDate)
    .lastIndexOf(true);

  // Set selected card to the "active" project (last past project) based on reference date
  // Runs on initial load and when selectedMonth or projects change
  useEffect(() => {
    if (lastPastProjectIndex !== -1 && projects[lastPastProjectIndex]) {
      const activeProject = projects[lastPastProjectIndex];
      // Only update if the active project actually changed
      if (selectedCardId !== activeProject.milestone_id) {
        setSelectedCardId(activeProject.milestone_id);
        // Trigger the callback if provided
        if (onCardClick) {
          onCardClick(activeProject.milestone_id, 'auto');
        }
      }
    }
  }, [selectedMonth, projects, lastPastProjectIndex]);

  // Get line bar status (always based on date - for line coloring)
  const getLineBarStatus = (projectDateStr: string, index: number) => {
    const projectDate = parseCompactDate(projectDateStr);
    if (projectDate <= referenceDate) {
      // If this is the last past project, it's 'active' for line bar
      if (index === lastPastProjectIndex) {
        return 'active';
      }
      // Otherwise, it's 'completed'
      return 'completed';
    }
    // Future projects have no line bar status
    return '';
  };

  // Connector curve color follows the next row's first milestone progress.
  const getCurveColorForRow = (nextRowFirstIndex: number) => {
    if (nextRowFirstIndex < 0 || nextRowFirstIndex >= projects.length) {
      return 'transparent';
    }
    const status = getLineBarStatus(projects[nextRowFirstIndex].date, nextRowFirstIndex);
    return status === 'completed' || status === 'active'
      ? 'rgba(127, 111, 241, 0.5)'
      : '#EBECF1';
  };

  // Check if icon should be enlarged (based on selection)
  const isIconEnlarged = (milestoneId: string) => {
    return selectedCardId === milestoneId;
  };

  // Handler for card click
  const handleCardClick = (milestoneId: string, isFuture: boolean) => {
    // 如果是未来的项目或正在编辑中，禁止点击
    if (isFuture || isEditing) {
      return;
    }
    setSelectedCardId(milestoneId);
    if (onCardClick) {
      onCardClick(milestoneId, 'click');
    }
  };

  // We map over the projects first to get the correct index for status calculation
  const renderedProjects = projects.map((project, index) => {
    // Line bar status (for coloring) - always based on date
    const lineBarStatus = getLineBarStatus(project.date, index);
    const isFuture = lineBarStatus === ''; // 未来的项目没有 status class
    
    // Icon enlargement - based on selection
    const iconEnlarged = isIconEnlarged(project.milestone_id);
    const isGlobalLast = index === projects.length - 1;
    const isFirstRowFirst = index === 0;
    const isSingleItem = projects.length === 1;
    // 是否可点击：非未来项目 且 不在编辑状态
    const isClickable = !isFuture && !isEditing;
    
    return (
      <div 
        key={project.milestone_id} 
        className={clsx(
          'dbs-project-card',
          lineBarStatus, // For line bar coloring (active/completed based on date)
          {
            'icon-enlarged': iconEnlarged, // For icon size (based on selection)
            'clickable': onCardClick && isClickable, // 只有非未来且非编辑中的项目才显示可点击样式
            'is-global-last': isGlobalLast,
            'is-first-row-first': isFirstRowFirst,
            'is-single-item': isSingleItem,
            // 'selected': selectedCardId === project.milestone_id
          }
        )}
        onClick={() => handleCardClick(project.milestone_id, isFuture)}
        style={{ cursor: isClickable && onCardClick ? 'pointer' : 'default' }}
      >
        <h3 className="dbs-project-title">{project.milestone}</h3>
        <p className="dbs-project-date">{formatCompactDateString(project.date, project.date_confirm)}</p>
        <div className="dbs-project-bar">
          <div className="dbs-project-line"></div>
        </div>
        <div className="dbs-project-location"></div>
        <div className="dbs-project-arrow"></div>
      </div>
    );
  });

  // Chunk cards by row size so every row keeps a stable layout.
  const projectRows: React.ReactNode[][] = [];
  for (let i = 0; i < renderedProjects.length; i += ROW_SIZE) {
    projectRows.push(renderedProjects.slice(i, i + ROW_SIZE));
  }

  return (
    <div className="dbs-project-calendar">
      {projectRows.map((row, rowIndex) => {
        const hasNextRow = rowIndex < projectRows.length - 1;
        const nextRowFirstIndex = (rowIndex + 1) * ROW_SIZE;
        return (
          <div
            key={`project-row-${rowIndex}`}
            className={clsx('dbs-project-calendar-list', { 'has-curve': hasNextRow })}
            style={{
              '--curve-color': hasNextRow ? getCurveColorForRow(nextRowFirstIndex) : 'transparent',
            } as React.CSSProperties}
          >
            {row}
          </div>
        );
      })}
    </div>
  );
};

export default ProjectCalendar;
